import { supabase } from "@/lib/supabase";
import {
  assignInitialProfileRole,
  normalizeRole,
  type UserRole,
} from "@/lib/services/role.service";
import { resolvePostVerifyRedirect } from "@/lib/utils/post-verify-redirect";

const SIGNUP_ROLE_KEY = "rakb_signup_role";

export const peekSignupRole = (): "owner" | "renter" | null => {
  try {
    const value = sessionStorage.getItem(SIGNUP_ROLE_KEY);
    return value === "owner" || value === "renter" ? value : null;
  } catch {
    return null;
  }
};

/** Keeps the chosen signup role across the Google redirect (same tab). */
export const rememberSignupRole = (role: string | null | undefined): void => {
  try {
    if (role === "owner" || role === "renter") {
      sessionStorage.setItem(SIGNUP_ROLE_KEY, role);
      return;
    }
    sessionStorage.removeItem(SIGNUP_ROLE_KEY);
  } catch {
    // sessionStorage unavailable (private mode)
  }
};

export const consumeSignupRole = (): "owner" | "renter" | null => {
  const value = peekSignupRole();
  try {
    sessionStorage.removeItem(SIGNUP_ROLE_KEY);
  } catch {
    // ignore
  }
  return value;
};

/**
 * Renters resume the car or search they came from.
 * A new agency opens host document verification. An already verified agency
 * goes to the owner dashboard. Neither path is the renter verification hub.
 */
export const resolveSignupLandingPath = (
  role: string | null | undefined,
  redirectTo: string | null | undefined,
  options?: { hostVerified?: boolean }
): string => {
  if (normalizeRole(role) === "owner") {
    return options?.hostVerified ? "/dashboard/owner" : "/documents/verification";
  }
  return resolvePostVerifyRedirect(redirectTo);
};

/**
 * Sets renter/owner only while profiles.role is still empty.
 * An existing role is left untouched.
 */
export const applyIntendedSignupRole = async (
  userId: string,
  intended: "owner" | "renter" | null
): Promise<UserRole | null> => {
  if (!intended) return null;

  const { data: profile, error } = await supabase
    .from("profiles")
    .select("role")
    .eq("id", userId)
    .maybeSingle();

  if (error) return null;

  const current = normalizeRole(profile?.role);
  if (current) return current;

  if (!profile) {
    await supabase.rpc("get_or_create_profile");
    const { data: created } = await supabase
      .from("profiles")
      .select("role")
      .eq("id", userId)
      .maybeSingle();
    const createdRole = normalizeRole(created?.role);
    if (createdRole) return createdRole;
  }

  return assignInitialProfileRole(userId, intended);
};

/** After email confirmation: host documents for a new agency, otherwise the renter return path. */
export const resolveSignupLandingForCurrentUser = async (
  redirectTo: string | null | undefined
): Promise<string> => {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    return resolvePostVerifyRedirect(redirectTo);
  }

  const { data: profile } = await supabase
    .from("profiles")
    .select("role, verified_host")
    .eq("id", user.id)
    .maybeSingle();

  const profileRole = normalizeRole(profile?.role);
  const hostVerified = profile?.verified_host === true;
  if (profileRole) {
    consumeSignupRole();
    return resolveSignupLandingPath(profileRole, redirectTo, { hostVerified });
  }

  const metadataRole = normalizeRole(
    typeof user.user_metadata?.role === "string" ? user.user_metadata.role : null
  );
  const storedRole = peekSignupRole();
  const intended =
    metadataRole === "owner" || metadataRole === "renter" ? metadataRole : storedRole;

  if (intended) {
    await applyIntendedSignupRole(user.id, intended);
  }
  consumeSignupRole();
  return resolveSignupLandingPath(intended, redirectTo);
};
